import cytoscape from 'cytoscape';
import edgehandles from 'cytoscape-edgehandles';
import dagre from 'cytoscape-dagre';
import {register} from './NodeHtml';
import {CytoGraphModel} from './CytoGraphModel';
import {CtxMenuArgs, EdgeCreatedArgs, NodeMoveArgs, TargetType} from './CytoGraphTypes';

cytoscape.use(edgehandles);
cytoscape.use(dagre);
// register node-html-label
register(cytoscape);

export class CytoscapeInteractions {
    private edgeHandler = null;
    private isEdgeMode: boolean = false;
    private ctxMenuCallback: (args: CtxMenuArgs) => void;
    private nodeSelectCallback: (id: string) => void;
    private nodeDblClickCallback: (id: string) => void;
    private nodeMovedCallback: (args: NodeMoveArgs) => void;
    private edgeCreatingCallback: (args: EdgeCreatedArgs) => boolean;
    private clicked: any;
    private dblClickTimeout = 300;

    registerHandlers(graphModel: CytoGraphModel) {
        this.registerEdgeClick(graphModel.cyObj);
        this.registerGraphClick(graphModel.cyObj);
        this.registerNodeClick(graphModel.cyObj, id => graphModel.onNodeClicked(id));
        this.registerNodeDblClick(graphModel.cyObj, id => graphModel.onNodeDblClicked(id));
        this.registerNodeMove(graphModel.cyObj, args => graphModel.onNodeMoved(args));
        this.registerMouseOver(graphModel.cyObj);
        this.registerCtxClick(graphModel.cyObj, args => graphModel.onCtxMenu(args));
        this.registerEdgeHandler(graphModel.cyObj, args => graphModel.onEdgeCreated(args));
    }

    private registerEdgeClick(cyObj: cytoscape.Core) {
        cyObj.on('tapstart cxttap', 'edge', e => {
            cyObj
                .elements(':selected')
                .filter(element => element.id() !== e.target.id())
                .unselect();
            this.nodeSelectCallback?.(null);
        });
    }

    private registerGraphClick(cyObj: cytoscape.Core) {
        cyObj.on('tapstart cxttap', e => {
            if (e.target !== cyObj) return;
            if (e.originalEvent.shiftKey || e.originalEvent.ctrlKey) return;

            cyObj.elements(':selected').unselect();
            this.nodeSelectCallback?.(null);
        });
    }

    private registerNodeClick(cyObj: cytoscape.Core, callback) {
        this.nodeSelectCallback = callback;

        cyObj.on('tap', 'node', e => {
            this.nodeSelectCallback(e.target.id());
        });
    }

    private registerNodeDblClick(cyObj: cytoscape.Core, callback) {
        this.nodeDblClickCallback = callback;
        cyObj.on('tap', 'node', e => {
            if (this.clicked && this.clicked === e.target) {
                this.clicked = null;
                e.preventDefault();
                e.stopPropagation();
                this.nodeDblClickCallback?.(e.target.id());
            } else {
                this.clicked = e.target;
                setTimeout(() => {
                    if (this.clicked && this.clicked === e.target) {
                        this.clicked = null;
                    }
                }, this.dblClickTimeout);
            }
        });
    }

    private registerNodeMove(cyObj: cytoscape.Core, callback) {
        this.nodeMovedCallback = callback;

        cyObj.on('dragfree', e => {
            this.nodeMovedCallback?.({id: e.target.id(), position: e.target.position()});
        });
    }

    private registerMouseOver(cyObj: cytoscape.Core) {
        cyObj.on('mouseover', 'node', e => {
            if (!e.target.selected()) e.target.addClass('hover');
        });

        cyObj.on('mouseout tap', 'node', e => e.target.removeClass('hover'));

        cyObj.on('mouseover', 'edge', e => e.target.addClass('hover'));

        cyObj.on('mouseout', 'edge', e => e.target.removeClass('hover'));
    }

    private registerCtxClick(cyObj: cytoscape.Core, callback) {
        this.ctxMenuCallback = callback;

        cyObj.on('cxttap', e => {
            if (!e.target.length) {
                this.ctxMenuCallback?.({
                    targetType: TargetType.TARGET_TYPE_GRAPH,
                    menuOffset: {x: e.renderedPosition.x, y: e.renderedPosition.y},
                    graphOffset: {x: e.position.x, y: e.position.y},
                    targetId: null
                });
            }
        });

        cyObj.on('cxttap', 'node', e => {
            e.target[0].select();
            this.nodeSelectCallback(e.target.id());

            const selectedNodes = cyObj
                .elements('node:selected')
                .filter(n => n.id() !== e.target.id());
            selectedNodes.unselect();

            this.nodeDblClickCallback?.(e.target.id());

            this.ctxMenuCallback?.({
                targetType: TargetType.TARGET_TYPE_NODE,
                menuOffset: {x: e.renderedPosition.x, y: e.renderedPosition.y},
                graphOffset: {x: 0, y: 0},
                targetId: e.target.id()
            });
        });

        cyObj.on('cxttap', 'edge', e => {
            this.ctxMenuCallback?.({
                targetType: TargetType.TARGET_TYPE_EDGE,
                menuOffset: {x: e.renderedPosition.x, y: e.renderedPosition.y},
                graphOffset: {x: 0, y: 0},
                targetId: e.target.id()
            });
        });
    }

    private registerEdgeHandler(
        cyObj: cytoscape.Core,
        callback: (args: EdgeCreatedArgs) => boolean
    ) {
        // @ts-ignore
        this.edgeHandler = cyObj.edgehandles();
        this.edgeCreatingCallback = callback;

        window.onkeydown = evt => {
            if (this.isEdgeMode) return;
            if (evt.shiftKey) {
                this.enableDrawMode();
            }
        };

        window.onkeyup = evt => {
            if (evt.key === 'Shift') this.disableDrawMode();
        };

        cyObj.on('ehstop', evt => this.disableDrawMode());

        cyObj.on('ehcomplete', (evt, source, target, edge) => {
            const data = edge.data();
            if (!data.style) data.style = {};
            edge.data(data);
            const isValid = this.edgeCreatingCallback?.({
                id: data.id,
                source: data.source,
                target: data.target
            });
            if (!isValid) cyObj.remove(edge);
        });
    }

    private enableDrawMode() {
        this.isEdgeMode = true;
        this.edgeHandler.enableDrawMode();
    }

    private disableDrawMode() {
        this.isEdgeMode = false;
        this.edgeHandler.disableDrawMode();
    }
}
